import React, { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { sendConversationFeedback } from '../../../chat_history/actions';
import { dismissFeedback, feedbackFailed, feedbackRequested, openSlidePanel, setFeedbackModal } from '../../actions';
import './feedback-progress.css';
/**
 * ★2026-10-12 「会話を評価」の進行表示。
 *
 * 押しても何も起きないように見える（ボタンの文字が変わるだけ）という指摘への対応。
 *   1. 押した瞬間にポップアップ：段階・進捗・経過秒・目安時間
 *   2. 「閉じて待つ」で閉じても、画面上部に「評価中… n秒」のピルが残る
 *   3. レポートが届いたら「できました」→ 2 秒後に自動でスライドパネルを開く
 *   4. 送れなかった／時間切れは理由と「もう一度試す」
 *
 * 進捗はサーバーから来ないので、経過時間から見せている（目安 20〜40 秒、95% で止めて待つ）。
 * デモ: /demo/converation-analysis-dev/
 */
/** これを過ぎても届かなければ失敗扱いにする。 */
const TIMEOUT_MS = 120000;
/** 進捗バーの目安。ここまでで 95% に達し、以降は待つ。 */
const EXPECTED_MS = 40000;
/** 「できました」から自動で開くまでの猶予。 */
const AUTO_OPEN_SEC = 2;
/** 段階の切り替え（経過 ms）。 */
const STAGE_SEND_MS = 2000;
const STAGE_READ_MS = 15000;
const STEPS = ['会話をAIに送信', 'AIが内容を読んでいます', 'レポート（PDF）を作成中', '完了'];
/**
 * 進行ポップアップ本体。
 *
 * @param {IProps} props - Props.
 * @returns {ReactElement|null}
 */
function FeedbackProgressModal({ _feedback, _score }) {
    const dispatch = useDispatch();
    const { phase, modal, startedAt, error } = _feedback;
    const [now, setNow] = useState(Date.now());
    const [autoLeft, setAutoLeft] = useState(AUTO_OPEN_SEC);
    // 経過秒を進める（依頼中だけ）。
    useEffect(() => {
        if (phase !== 'running') {
            return;
        }
        const id = window.setInterval(() => setNow(Date.now()), 250);
        return () => window.clearInterval(id);
    }, [phase]);
    // 時間切れ。
    useEffect(() => {
        if (phase !== 'running' || !startedAt) {
            return;
        }
        const left = TIMEOUT_MS - (Date.now() - startedAt);
        const id = window.setTimeout(() => {
            dispatch(feedbackFailed('AIからの応答がありませんでした。しばらくしてからもう一度お試しください。'));
        }, Math.max(0, left));
        return () => window.clearTimeout(id);
    }, [phase, startedAt, dispatch]);
    const open = useCallback(() => {
        dispatch(openSlidePanel());
        dispatch(dismissFeedback());
    }, [dispatch]);
    // 届いたら 2 秒後に自動で開く（「あとで見る」で止まる）。
    useEffect(() => {
        if (phase !== 'done') {
            return;
        }
        setAutoLeft(AUTO_OPEN_SEC);
        let left = AUTO_OPEN_SEC;
        const id = window.setInterval(() => {
            left -= 1;
            setAutoLeft(left);
            if (left <= 0) {
                window.clearInterval(id);
                open();
            }
        }, 1000);
        return () => window.clearInterval(id);
    }, [phase, open]);
    const hide = useCallback(() => dispatch(setFeedbackModal(false)), [dispatch]);
    const stop = useCallback((e) => e.stopPropagation(), []);
    const show = useCallback(() => dispatch(setFeedbackModal(true)), [dispatch]);
    const later = useCallback(() => dispatch(dismissFeedback()), [dispatch]);
    const retry = useCallback(() => {
        const sent = dispatch(sendConversationFeedback());
        dispatch(sent
            ? feedbackRequested()
            : feedbackFailed('会話がまだ無いか、AIが参加していません。少し話してからもう一度お試しください。'));
    }, [dispatch]);
    if (phase === 'idle') {
        return null;
    }
    const elapsedMs = startedAt ? Math.max(0, now - startedAt) : 0;
    const elapsedSec = Math.floor(elapsedMs / 1000);
    const progress = phase === 'done' ? 100 : Math.min(95, Math.round(elapsedMs / EXPECTED_MS * 100));
    let stage = 0;
    if (phase === 'done') {
        stage = 4;
    }
    else if (elapsedMs >= STAGE_READ_MS) {
        stage = 2;
    }
    else if (elapsedMs >= STAGE_SEND_MS) {
        stage = 1;
    }
    // 閉じて待っている間のピル。
    if (!modal) {
        if (phase !== 'running') {
            return null;
        }
        return (React.createElement("div", { className: 'fbp-pill' },
            React.createElement("span", { className: 'fbp-spin' }),
            React.createElement("span", null,
                "\u8A55\u4FA1\u4E2D\u2026 ",
                elapsedSec,
                "\u79D2"),
            React.createElement("button", { onClick: show, type: 'button' }, "\u8A73\u7D30")));
    }
    return (React.createElement("div", { className: 'fbp-overlay', onClick: phase === 'running' ? hide : undefined },
        React.createElement("div", { "aria-live": 'polite', className: 'fbp-modal', onClick: stop, role: 'dialog' }, phase === 'done' ? (React.createElement(React.Fragment, null,
            React.createElement("h3", { className: 'fbp-title' }, "\u2705 \u30EC\u30DD\u30FC\u30C8\u304C\u3067\u304D\u307E\u3057\u305F"),
            typeof _score === 'number' && (React.createElement("div", { className: 'fbp-score' },
                React.createElement("b", null, _score),
                React.createElement("span", null, "/ 5"),
                React.createElement("span", { className: 'fbp-stars' },
                    '★'.repeat(Math.max(0, Math.min(5, Math.round(_score)))),
                    '☆'.repeat(5 - Math.max(0, Math.min(5, Math.round(_score))))))),
            React.createElement("p", { className: 'fbp-sub' }, autoLeft > 0 ? `${autoLeft}秒後に自動で開きます。` : '開いています…'),
            React.createElement("div", { className: 'fbp-row' },
                React.createElement("button", { className: 'fbp-btn', onClick: later, type: 'button' }, "\u3042\u3068\u3067\u898B\u308B"),
                React.createElement("button", { className: 'fbp-btn fbp-primary', onClick: open, type: 'button' }, "\u30EC\u30DD\u30FC\u30C8\u3092\u958B\u304F")))) : (React.createElement(React.Fragment, null,
            React.createElement("h3", { className: 'fbp-title' }, "\uD83E\uDD16 AI\u304C\u4F1A\u8A71\u3092\u8A55\u4FA1\u3057\u3066\u3044\u307E\u3059"),
            React.createElement("p", { className: 'fbp-sub' }, "\u3053\u3053\u307E\u3067\u306E\u4F1A\u8A71\u3092\u3082\u3068\u306B\u30EC\u30DD\u30FC\u30C8\uFF08PDF\uFF09\u3092\u4F5C\u6210\u3057\u307E\u3059\u3002\u901A\u5E38 20\u301C40 \u79D2\u3067\u3059\u3002"),
            React.createElement("ul", { className: 'fbp-steps' }, STEPS.map((label, i) => (React.createElement("li", { className: i < stage ? 'done' : (i === stage && phase === 'running' && i < 3 ? 'active' : ''), key: label },
                React.createElement("span", { className: 'fbp-dot' }),
                label)))),
            React.createElement("div", { className: 'fbp-track' },
                React.createElement("div", { className: 'fbp-fill', style: { width: `${progress}%` } })),
            React.createElement("div", { className: 'fbp-meta' },
                React.createElement("span", null,
                    progress,
                    "%"),
                React.createElement("span", null,
                    "\u7D4C\u904E ",
                    elapsedSec,
                    "\u79D2")),
            phase === 'failed' && (React.createElement("div", { className: 'fbp-err' },
                React.createElement("b", null, "\u4F5C\u6210\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002"),
                " ",
                error)),
            React.createElement("div", { className: 'fbp-row' },
                phase === 'failed' && (React.createElement("button", { className: 'fbp-btn', onClick: retry, type: 'button' }, "\u3082\u3046\u4E00\u5EA6\u8A66\u3059")),
                React.createElement("button", { className: 'fbp-btn', onClick: phase === 'failed' ? later : hide, type: 'button' }, phase === 'failed' ? '閉じる' : '閉じて待つ')))))));
}
/**
 * Redux state → props.
 *
 * @param {IReduxState} state - The Redux state.
 * @returns {Object}
 */
function _mapStateToProps(state) {
    const sv = state['features/slide-view'];
    return {
        _feedback: sv?.feedback ?? { error: '', modal: false, phase: 'idle', startedAt: null },
        _score: sv?.kind === 'feedback_report' ? (sv?.score ?? null) : null
    };
}
export default connect(_mapStateToProps)(FeedbackProgressModal);
